import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { formatBRL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, revalidateStock } = useCart();

  useEffect(() => {
    if (open) revalidateStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="p-4 border-b">
          <SheetTitle className="text-lg font-bold flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" /> Meu carrinho ({items.length})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center">
            <ShoppingBag className="h-14 w-14 text-neutral-300" />
            <p className="text-neutral-600">Seu carrinho está vazio</p>
            <Button onClick={() => setOpen(false)} className="bg-[#C91F1F] hover:bg-[#9F1515]">Continuar comprando</Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {items.map((it) => {
                const max = Number(it.max_stock ?? 99);
                const atMax = it.quantity >= max;
                return (
                  <div key={it.key} data-testid={`cart-item-${it.product_id}`} className="flex gap-3 border-b pb-4 last:border-none">
                    <img src={it.image} alt={it.name} className="w-20 h-24 object-cover rounded-md bg-neutral-100" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium line-clamp-2">{it.name}</p>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        {[it.size, it.color].filter(Boolean).join(" • ")}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center border rounded-md">
                          <button onClick={() => updateQty(it.key, it.quantity - 1)} className="p-1.5 hover:bg-neutral-100 disabled:opacity-50" disabled={it.quantity <= 1} aria-label="Diminuir">
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="px-3 text-sm font-medium" data-testid={`cart-qty-${it.product_id}`}>{it.quantity}</span>
                          <button onClick={() => updateQty(it.key, it.quantity + 1)} className="p-1.5 hover:bg-neutral-100 disabled:opacity-40" disabled={atMax} aria-label="Aumentar">
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <span className="font-bold text-[#C91F1F]">{formatBRL(it.price * it.quantity)}</span>
                      </div>
                      {atMax && (
                        <p className="text-[11px] text-amber-700 mt-1" data-testid={`cart-max-${it.product_id}`}>Máximo em estoque: {max}</p>
                      )}
                    </div>
                    <button onClick={() => removeItem(it.key)} className="text-neutral-400 hover:text-[#C91F1F] p-1" aria-label="Remover">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="border-t p-4 space-y-3 bg-neutral-50">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span className="font-medium">{formatBRL(subtotal)}</span>
              </div>
              <p className="text-xs text-neutral-500">Retirada grátis na loja Magé-RJ</p>
              <Link to="/checkout" onClick={() => setOpen(false)}>
                <Button data-testid="cart-checkout-btn" className="w-full bg-[#C91F1F] hover:bg-[#9F1515] h-12 text-base font-semibold">
                  Finalizar compra • {formatBRL(subtotal)}
                </Button>
              </Link>
              <Button variant="outline" onClick={() => setOpen(false)} className="w-full">Continuar comprando</Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
